/**
 * Timeout Management Utility for Wealthica2 Integration
 * 
 * Provides centralized timeout management with environment-based adjustments
 * and callback timeout protection for long-running sessions.
 */

import { logger } from '@/services/LoggingService';

export interface TimeoutConfig {
  // Query timeouts
  queryStaleTime: number;
  queryGcTime: number;
  dynamicDataStaleTime: number;
  accountDataStaleTime: number;
  
  // Cache timeouts
  cacheTtl: number;
  
  // Persistence timeouts
  debounceThreshold: number;
  persistenceCleanupDelay: number;
  
  // Callback timeouts
  callbackTimeout: number;
  tokenRefreshTimeout: number;
}

/**
 * Environment-based timeout configurations
 */
const TIMEOUT_CONFIGS: Record<string, TimeoutConfig> = {
  development: {
    // Extended timeouts for development/long sessions
    queryStaleTime: 30 * 60 * 1000,      // 30 minutes
    queryGcTime: 4 * 60 * 60 * 1000,     // 4 hours
    dynamicDataStaleTime: 5 * 60 * 1000, // 5 minutes
    accountDataStaleTime: 15 * 60 * 1000, // 15 minutes
    cacheTtl: 2 * 60 * 60 * 1000,        // 2 hours
    debounceThreshold: 5000,              // 5 seconds
    persistenceCleanupDelay: 30000,       // 30 seconds
    callbackTimeout: 30000,               // 30 seconds
    tokenRefreshTimeout: 15000,           // 15 seconds
  },
  
  production: {
    // Production-optimized timeouts
    queryStaleTime: 10 * 60 * 1000,      // 10 minutes
    queryGcTime: 30 * 60 * 1000,         // 30 minutes
    dynamicDataStaleTime: 2 * 60 * 1000, // 2 minutes
    accountDataStaleTime: 5 * 60 * 1000,  // 5 minutes
    cacheTtl: 15 * 60 * 1000,            // 15 minutes
    debounceThreshold: 2000,              // 2 seconds
    persistenceCleanupDelay: 10000,       // 10 seconds
    callbackTimeout: 30000,               // 30 seconds (Wealthica can take up to 30s)
    tokenRefreshTimeout: 10000,           // 10 seconds
  }
};

/**
 * Get the current environment
 */
function getCurrentEnvironment(): string {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname.includes('127.0.0.1')) {
      return 'development';
    }
  }
  return 'production';
}

/**
 * Get timeout configuration for current environment
 */
export function getTimeoutConfig(): TimeoutConfig {
  const env = getCurrentEnvironment();
  const config = TIMEOUT_CONFIGS[env];
  
  logger.debug('TimeoutManager', 'Using timeout configuration', {
    environment: env,
    config
  });
  
  return config;
}

/**
 * Timeout manager for callback operations
 */
export class CallbackTimeoutManager {
  private timeouts: Map<string, NodeJS.Timeout> = new Map();
  private config: TimeoutConfig;
  
  constructor() {
    this.config = getTimeoutConfig();
  }
  
  /**
   * Set a timeout for callback processing with automatic cleanup
   */
  setCallbackTimeout(
    key: string, 
    onTimeout: () => void, 
    customTimeout?: number
  ): void {
    const timeout = customTimeout || this.config.callbackTimeout;
    
    // Clear existing timeout if any
    this.clearTimeout(key);
    
    const timeoutId = setTimeout(() => {
      logger.warn('CallbackTimeoutManager', 'Callback timeout triggered', { 
        key, 
        timeout 
      });
      onTimeout();
      this.timeouts.delete(key);
    }, timeout);
    
    this.timeouts.set(key, timeoutId);
    
    logger.debug('CallbackTimeoutManager', 'Set callback timeout', { 
      key, 
      timeout 
    });
  }
  
  /**
   * Clear a specific timeout
   */
  clearTimeout(key: string): void {
    const timeoutId = this.timeouts.get(key);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.timeouts.delete(key);
      logger.debug('CallbackTimeoutManager', 'Cleared timeout', { key });
    }
  }
  
  /**
   * Clear all timeouts
   */
  clearAllTimeouts(): void {
    this.timeouts.forEach((timeoutId, key) => {
      clearTimeout(timeoutId);
      logger.debug('CallbackTimeoutManager', 'Cleared timeout', { key });
    });
    this.timeouts.clear();
    logger.info('CallbackTimeoutManager', 'Cleared all timeouts');
  }
  
  /**
   * Check if a timeout exists
   */
  hasTimeout(key: string): boolean {
    return this.timeouts.has(key);
  }
}

// Export singleton instance
export const callbackTimeoutManager = new CallbackTimeoutManager();